import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { db, doc, onSnapshot, setDoc, deleteDoc } from '../components/FirebaseConfig';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import YouTube from 'react-youtube';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  Button,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SyncIcon from '@mui/icons-material/Sync';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import VotationModal from '../components/VotationModal';
import { apiBaseUrl, isSyncBackendAvailable } from '../services/api';
import { getAppTitle } from '../utils/version';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0b0c14',
      paper: '#141624',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Segoe UI", sans-serif',
  },
});

function Monitor({ user, updateUserField }) {
  const [videoId, setVideoId] = useState('');
  const location = useLocation();

  useEffect(() => {
    document.title = getAppTitle('Garaoke Monitor');
  }, []);
  const queryParams = new URLSearchParams(location.search);
  const getStoredUid = () => {
    try {
      // 1. Host persistido del monitor o del anfitrión
      const monitorHost = localStorage.getItem('garaoke_monitor_host_id');
      if (monitorHost) return monitorHost;
      const hostId = localStorage.getItem('garaoke_host_id');
      if (hostId) return hostId;
      // 2. Token de autenticación de la aplicación
      const tokenStr = localStorage.getItem('garaoke-auth-token');
      if (tokenStr) {
        const parsed = JSON.parse(tokenStr);
        if (parsed?.user_id) return parsed.user_id;
        if (parsed?.uid) return parsed.uid;
      }
      // 3. Claves de Firebase Auth persistidas en localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('firebase:authUser:')) {
          const fbData = JSON.parse(localStorage.getItem(key));
          if (fbData?.uid) return fbData.uid;
        }
      }
      // 4. Fallback en garaoke_user
      const saved = localStorage.getItem('garaoke_user');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.uid) return parsed.uid;
      }
    } catch (e) { }
    return null;
  };

  const effectiveUserId = queryParams.get('UID')
    || queryParams.get('uid')
    || queryParams.get('userId')
    || user?.uid
    || getStoredUid();

  useEffect(() => {
    if (effectiveUserId) {
      try {
        localStorage.setItem('garaoke_monitor_host_id', effectiveUserId);
      } catch (e) { }
    }
  }, [effectiveUserId]);

  // Estado del video y sincronización
  const [currentVideoData, setCurrentVideoData] = useState(null);
  const [currentVideoDataAux, setCurrentVideoDataAux] = useState(null);
  const [playbackState, setPlaybackState] = useState({
    videoId: null,
    status: 'STOPPED',
    baseTime: 0,
    startedAt: Date.now(),
    playbackRate: 1,
    duration: 0,
    assignedTo: '',
    title: '',
  });

  const [tar, setTar] = useState(null);
  const [totalVideoTime, setTotalVideoTime] = useState(0);
  const [currentProgressTime, setCurrentProgressTime] = useState(0);
  const [showVotation, setShowVotation] = useState(false);
  const [stars, setStars] = useState(0);
  const [playlist, setPlaylist] = useState([]);
  const [syncStatus, setSyncStatus] = useState('connecting'); // 'live', 'connecting', 'offline'
  const [height, setHeight] = useState(window.innerHeight);
  const [syncOffsetSec, setSyncOffsetSec] = useState(() => {
    const envVal = parseFloat(import.meta.env?.VITE_MONITOR_SYNC_OFFSET_SEC);
    return !isNaN(envVal) ? envVal : 0;
  });
  const maxUserTolerance = useMemo(() => {
    const envVal = parseFloat(import.meta.env?.VITE_MAX_USER_SYNC_TOLERANCE_SEC ?? process.env?.VITE_MAX_USER_SYNC_TOLERANCE_SEC);
    return !isNaN(envVal) && envVal > 0 ? envVal : 10;
  }, []);
  const bufferLatency = useMemo(() => {
    const envVal = parseFloat(import.meta.env?.VITE_YOUTUBE_BUFFER_LATENCY_SEC ?? process.env?.VITE_YOUTUBE_BUFFER_LATENCY_SEC);
    return !isNaN(envVal) && envVal >= 0 ? envVal : 2.0;
  }, []);
  const adjustStep = useMemo(() => {
    const envVal = parseFloat(import.meta.env?.VITE_SYNC_ADJUST_STEP_SEC ?? process.env?.VITE_SYNC_ADJUST_STEP_SEC);
    return !isNaN(envVal) && envVal > 0 ? envVal : 0.5;
  }, []);
  const [userTolerance, setUserTolerance] = useState(() => {
    const saved = parseFloat(localStorage.getItem('garaoke_user_sync_tolerance'));
    const envMax = parseFloat(import.meta.env?.VITE_MAX_USER_SYNC_TOLERANCE_SEC ?? process.env?.VITE_MAX_USER_SYNC_TOLERANCE_SEC) || 10;
    return !isNaN(saved) ? Math.max(-envMax, Math.min(envMax, saved)) : 0;
  });
  const [isMonitorEnabled, setIsMonitorEnabled] = useState(true);

  // Helper de resolución exhaustiva del cantante asignado a la pista
  const resolveAssignedSinger = useCallback((vidId, currentPl, rawCvd, rawCps) => {
    if (rawCvd?.assignedTo && typeof rawCvd.assignedTo === 'string' && rawCvd.assignedTo.trim() && rawCvd.assignedTo.trim().toLowerCase() !== 'cantante') {
      return rawCvd.assignedTo.trim();
    }
    if (rawCps?.assignedTo && typeof rawCps.assignedTo === 'string' && rawCps.assignedTo.trim() && rawCps.assignedTo.trim().toLowerCase() !== 'cantante') {
      return rawCps.assignedTo.trim();
    }
    if (vidId && Array.isArray(currentPl)) {
      const match = currentPl.find(it => (it.videoId === vidId || it.id === vidId || it.item?.videoId === vidId));
      const plSinger = match?.assignedTo || match?.item?.assignedTo || match?.singer || match?.performer;
      if (typeof plSinger === 'string' && plSinger.trim() && plSinger.trim().toLowerCase() !== 'cantante') {
        return plSinger.trim();
      }
    }
    return '';
  }, []);

  // Escuchar cambios de tolerancia personalizada del usuario
  useEffect(() => {
    const handleTolChange = (e) => {
      if (typeof e.detail?.tolerance === 'number') {
        setUserTolerance(e.detail.tolerance);
      }
    };
    window.addEventListener('garaoke_user_tolerance_change', handleTolChange);
    return () => window.removeEventListener('garaoke_user_tolerance_change', handleTolChange);
  }, []);

  // Posición de tiempo remota y videoId provenientes de Firebase (fuente de verdad directa del Player)
  const [fbVideoTime, setFbVideoTime] = useState(0);
  const [fbVideoId, setFbVideoId] = useState(null);

  const containerRef = useRef(null);
  const lastVotedSongRef = useRef(null);
  const lastManualTriggerIdRef = useRef(null);
  const prevSyncOffsetRef = useRef(0);
  const driftThreshold = parseFloat(import.meta.env.VITE_SYNC_DRIFT_THRESHOLD_SEC) || 2.5;

  // Escuchar disparos de votación manual locales vía BroadcastChannel y CustomEvent
  useEffect(() => {
    let syncChannel = null;
    try {
      syncChannel = new BroadcastChannel('garaoke_player_sync');
      syncChannel.onmessage = (event) => {
        if (event?.data?.type === 'TRIGGER_MANUAL_VOTATION') {
          const trigger = event.data;
          console.log('🗳️ [Monitor] BroadcastChannel TRIGGER_MANUAL_VOTATION:', trigger);
          if (trigger.triggerId && lastManualTriggerIdRef.current === trigger.triggerId) return;
          if (trigger.triggerId) lastManualTriggerIdRef.current = trigger.triggerId;
          if (trigger.videoId) {
            const finalSinger = (trigger.assignedTo && trigger.assignedTo.trim().toLowerCase() !== 'cantante')
              ? trigger.assignedTo.trim()
              : (resolveAssignedSinger(trigger.videoId, playlist, null, playbackState) || '');
            setCurrentVideoData(prev => ({
              videoId: trigger.videoId,
              assignedTo: finalSinger || prev?.assignedTo || '',
              title: trigger.title || prev?.title || '',
            }));
            setCurrentVideoDataAux(prev => ({
              videoId: trigger.videoId,
              assignedTo: finalSinger || prev?.assignedTo || '',
              playlist: prev?.playlist || 'user_playlist',
            }));
          }
          setShowVotation(true);
        } else if (event?.data?.type === 'playbackProgress') {
          const { videoId: progVid, currentTime: progTime } = event.data;
          if (typeof progTime === 'number' && progTime >= 0) {
            setFbVideoTime(progTime);
            if (progVid) setFbVideoId(progVid);
            if (tar && typeof tar.getCurrentTime === 'function' && playbackState.status === 'PLAYING') {
              const localTime = tar.getCurrentTime();
              const targetTime = Math.max(0, progTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0));
              const drift = Math.abs(localTime - targetTime);
              if (drift > driftThreshold && targetTime < (totalVideoTime || 99999)) {
                console.log(`[Monitor Sync Broadcast] Salto detectado en anfitrión (${localTime.toFixed(1)}s -> ${targetTime.toFixed(1)}s, drift: ${drift.toFixed(1)}s). Sincronizando monitor...`);
                tar.seekTo(targetTime, true);
                if (typeof tar.playVideo === 'function') tar.playVideo();
              }
            }
          }
        }
      };
    } catch (e) { }

    const handleCustomPlaybackProgress = (e) => {
      const data = e?.detail;
      if (data && typeof data.currentTime === 'number' && data.currentTime >= 0) {
        setFbVideoTime(data.currentTime);
        if (data.videoId) setFbVideoId(data.videoId);
        if (tar && typeof tar.getCurrentTime === 'function' && playbackState.status === 'PLAYING') {
          const localTime = tar.getCurrentTime();
          const targetTime = Math.max(0, data.currentTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0));
          const drift = Math.abs(localTime - targetTime);
          if (drift > driftThreshold && targetTime < (totalVideoTime || 99999)) {
            console.log(`[Monitor Sync CustomEvent] Salto detectado en anfitrión (${localTime.toFixed(1)}s -> ${targetTime.toFixed(1)}s). Sincronizando monitor...`);
            tar.seekTo(targetTime, true);
            if (typeof tar.playVideo === 'function') tar.playVideo();
          }
        }
      }
    };
    window.addEventListener('garaoke_playback_progress', handleCustomPlaybackProgress);

    const handleCustomManualVotation = (e) => {
      const trigger = e?.detail;
      if (trigger) {
        console.log('🗳️ [Monitor] CustomEvent garaoke_manual_votation:', trigger);
        if (trigger.triggerId && lastManualTriggerIdRef.current === trigger.triggerId) return;
        if (trigger.triggerId) lastManualTriggerIdRef.current = trigger.triggerId;
        if (trigger.videoId) {
          const finalSinger = (trigger.assignedTo && trigger.assignedTo.trim().toLowerCase() !== 'cantante')
            ? trigger.assignedTo.trim()
            : (resolveAssignedSinger(trigger.videoId, playlist, null, playbackState) || '');
          setCurrentVideoData(prev => ({
            videoId: trigger.videoId,
            assignedTo: finalSinger || prev?.assignedTo || '',
            title: trigger.title || prev?.title || '',
          }));
          setCurrentVideoDataAux(prev => ({
            videoId: trigger.videoId,
            assignedTo: finalSinger || prev?.assignedTo || '',
            playlist: prev?.playlist || 'user_playlist',
          }));
        }
        setShowVotation(true);
      }
    };
    window.addEventListener('garaoke_manual_votation', handleCustomManualVotation);

    return () => {
      if (syncChannel) {
        try { syncChannel.close(); } catch (e) { }
      }
      window.removeEventListener('garaoke_manual_votation', handleCustomManualVotation);
      window.removeEventListener('garaoke_playback_progress', handleCustomPlaybackProgress);
    };
  }, [tar, playbackState.status, syncOffsetSec, userTolerance, driftThreshold, totalVideoTime]);

  const opts = useMemo(() => ({
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 1,
      controls: 0,
      enablejsapi: 1,
      modestbranding: 1,
      rel: 0,
      playsinline: 1,
      mute: 1,
      origin: typeof window !== 'undefined' ? window.location.origin : '',
    },
  }), []);

  // ── 1. Canal en Tiempo Real Híbrido: SSE (Express) + Fallback Firestore ──
  useEffect(() => {
    if (!effectiveUserId) return;

    let eventSource = null;
    const syncBackendEnabled = isSyncBackendAvailable;

    // Intentar conexión SSE para latencia <20ms y 0 lecturas en Firestore
    if (syncBackendEnabled) {
      try {
        const sseUrl = `${apiBaseUrl}/sync/events/${effectiveUserId}`;
        eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          setSyncStatus('live');
          console.log('⚡ Canal SSE conectado en tiempo real para el host:', effectiveUserId);
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'INITIAL_STATE' || data.type === 'STATE_CHANGE') {
              const state = data.state;
              if (state) {
                setPlaybackState(state);
                if (state.baseTime !== undefined && typeof state.baseTime === 'number') {
                  setFbVideoTime(state.baseTime);
                  if (tar && typeof tar.getCurrentTime === 'function' && state.status === 'PLAYING') {
                    const localTime = tar.getCurrentTime();
                    const targetTime = Math.max(0, state.baseTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0));
                    const drift = Math.abs(localTime - targetTime);
                    if (drift > driftThreshold && targetTime < (totalVideoTime || 99999)) {
                      console.log(`[Monitor Sync SSE] Salto detectado vía SSE (drift ${drift.toFixed(1)}s > ${driftThreshold}s) -> seekTo(${targetTime.toFixed(1)}s)`);
                      tar.seekTo(targetTime, true);
                      if (typeof tar.playVideo === 'function') tar.playVideo();
                    }
                  }
                }
                if (state.videoId) {
                  setVideoId(prev => prev === state.videoId ? prev : state.videoId);
                  let resolvedAssigned = (state.assignedTo || '').trim();
                  if (!resolvedAssigned || resolvedAssigned.toLowerCase() === 'cantante') {
                    resolvedAssigned = resolveAssignedSinger(state.videoId, playlist, null, state);
                  }
                  setCurrentVideoData({
                    videoId: state.videoId,
                    assignedTo: resolvedAssigned,
                    title: state.title || '',
                  });
                  setCurrentVideoDataAux({
                    videoId: state.videoId,
                    assignedTo: resolvedAssigned,
                    playlist: state.playlist || 'user_playlist',
                  });
                }
                // NOTA: No limpiar videoId/currentVideoData desde SSE si state.videoId es null,
                // porque durante crossfade/transiciones el Player emite STOPPED momentáneamente.
                // Solo Firestore (onSnapshot con currentVideoData=null explícito) puede limpiar.
                setSyncStatus('live');
              }
            }
          } catch (err) {
            console.warn('Error parseando SSE:', err);
          }
        };

        eventSource.onerror = () => {
          try {
            eventSource.close();
          } catch (e) { }
          setSyncStatus('live');
        };
      } catch (e) {
        console.warn('SSE no disponible, usando Firestore...');
      }
    }

    // Canal de persistencia Firestore (en paralelo con SSE, sin bloqueo mutuo)
    const docRef = doc(db, 'users', effectiveUserId);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const docData = docSnap.data();

        // 1. Estado de reproducción inteligente
        let activeVideoId = null;
        let activeStatus = 'STOPPED';
        let activeTitle = '';
        let activeAssignedTo = '';
        let activePlaylist = 'user_playlist';

        if (docData.currentPlaybackState) {
          try {
            const state = typeof docData.currentPlaybackState === 'string'
              ? JSON.parse(docData.currentPlaybackState)
              : docData.currentPlaybackState;

            if (state) {
              setPlaybackState(state);
              activeStatus = state.status || 'STOPPED';
              if (state.videoId) {
                activeVideoId = state.videoId;
                activeTitle = state.title || '';
                activeAssignedTo = state.assignedTo || '';
                activePlaylist = state.playlist || 'user_playlist';
              }
            }
          } catch (err) {
            console.warn('Error parseando currentPlaybackState en Monitor:', err);
          }
        }

        // Complementar con currentVideoData
        let parsedCvd = null;
        if (docData.currentVideoData) {
          try {
            parsedCvd = typeof docData.currentVideoData === 'string'
              ? JSON.parse(docData.currentVideoData)
              : docData.currentVideoData;
            if (parsedCvd) {
              if (!activeVideoId && parsedCvd.videoId) {
                activeVideoId = parsedCvd.videoId;
                activeStatus = 'PLAYING';
              }
              if (!activeTitle && parsedCvd.title) activeTitle = parsedCvd.title;
              if (!activeAssignedTo && parsedCvd.assignedTo) activeAssignedTo = parsedCvd.assignedTo;
            }
          } catch (err) { }
        }

        const effectivePlaylist = docData.playlist || playlist;
        if (docData.playlist) {
          setPlaylist(docData.playlist);
        }

        // Resolución exhaustiva si activeAssignedTo está vacío o es genérico
        if (activeVideoId && (!activeAssignedTo || activeAssignedTo.trim().toLowerCase() === 'cantante')) {
          activeAssignedTo = resolveAssignedSinger(activeVideoId, effectivePlaylist, parsedCvd, playbackState);
        }

        // Actualizar video si hay un videoId activo reproduciéndose
        if (activeVideoId && activeStatus === 'PLAYING') {
          setVideoId(activeVideoId);
          setCurrentVideoData({
            videoId: activeVideoId,
            assignedTo: activeAssignedTo,
            title: activeTitle,
          });
          setCurrentVideoDataAux({
            videoId: activeVideoId,
            assignedTo: activeAssignedTo,
            playlist: activePlaylist,
          });
        } else if (activeStatus === 'STOPPED') {
          // Solo limpiar si el Player emitió explícitamente STOPPED Y además
          // currentVideoData en Firebase también está vacío (para no limpiar por
          // cambios de config que disparan onSnapshot sin cambiar el estado de video)
          const cvd = docData.currentVideoData;
          let cvdHasVideo = false;
          if (cvd) {
            try {
              const parsed = typeof cvd === 'string' ? JSON.parse(cvd) : cvd;
              cvdHasVideo = !!(parsed && parsed.videoId);
            } catch (e) { }
          }
          if (!cvdHasVideo) {
            setVideoId('');
            setCurrentVideoData(null);
            setTotalVideoTime(0);
            setCurrentProgressTime(0);
          }
          // Si currentVideoData todavía tiene un video válido, no limpiar:
          // el Player probablemente está en transición y pronto emitirá PLAYING con nuevo video
        }

        if (docData.playlist) {
          setPlaylist(docData.playlist);
        }

        // Capturar heartbeat / salto de posición directo de Firebase (fuente de verdad para el Monitor)
        if (typeof docData.currentVideoTime === 'number' && docData.currentVideoTime >= 0) {
          const newFbTime = docData.currentVideoTime;
          setFbVideoTime(newFbTime);
          if (tar && typeof tar.getCurrentTime === 'function' && activeStatus === 'PLAYING') {
            const localTime = tar.getCurrentTime();
            const targetTime = Math.max(0, newFbTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0));
            const drift = Math.abs(localTime - targetTime);
            if (drift > driftThreshold && targetTime < (totalVideoTime || 99999)) {
              console.log(`[Monitor Sync Firestore] Salto detectado vía Firestore (${localTime.toFixed(1)}s -> ${targetTime.toFixed(1)}s, drift ${drift.toFixed(1)}s > ${driftThreshold}s) -> seekTo(${targetTime.toFixed(1)}s)`);
              tar.seekTo(targetTime, true);
              if (typeof tar.playVideo === 'function') tar.playVideo();
            }
          }
        }
        // Capturar el videoId correspondiente al heartbeat del Player
        if (docData.currentVideoId) {
          setFbVideoId(docData.currentVideoId);
        }

        // Leer tolerancia / desfasaje en segundos del documento del anfitrión
        if (docData.monitorSyncOffsetSec !== undefined || docData.configs?.general?.monitorSyncOffsetSec !== undefined) {
          const offsetVal = Number(docData.monitorSyncOffsetSec ?? docData.configs?.general?.monitorSyncOffsetSec ?? 0);
          setSyncOffsetSec(offsetVal);
        }

        // Verificar si el anfitrión tiene habilitado el modo monitor
        if (docData.isMonitorEnabled !== undefined || docData.configs?.general?.isMonitorEnabled !== undefined) {
          const enabled = (docData.isMonitorEnabled ?? docData.configs?.general?.isMonitorEnabled) !== false;
          setIsMonitorEnabled(enabled);
        }

        // Disparador de Votación Manual emitido desde el Panel o Control Remoto
        if (docData.manualVotationTrigger) {
          try {
            const trigger = typeof docData.manualVotationTrigger === 'string'
              ? JSON.parse(docData.manualVotationTrigger)
              : docData.manualVotationTrigger;

            if (trigger && trigger.timestamp && (Date.now() - trigger.timestamp < 60000)) {
              if (trigger.triggerId && lastManualTriggerIdRef.current !== trigger.triggerId) {
                lastManualTriggerIdRef.current = trigger.triggerId;
                console.log('🗳️ [Monitor Firestore] Disparo de votación manual recibido:', trigger);
                if (trigger.videoId) {
                  const finalSinger = (trigger.assignedTo && trigger.assignedTo.trim().toLowerCase() !== 'cantante')
                    ? trigger.assignedTo.trim()
                    : (resolveAssignedSinger(trigger.videoId, effectivePlaylist, parsedCvd, playbackState) || '');
                  setCurrentVideoData(prev => ({
                    videoId: trigger.videoId,
                    assignedTo: finalSinger || prev?.assignedTo || '',
                    title: trigger.title || prev?.title || '',
                  }));
                  setCurrentVideoDataAux(prev => ({
                    videoId: trigger.videoId,
                    assignedTo: finalSinger || prev?.assignedTo || '',
                    playlist: prev?.playlist || 'user_playlist',
                  }));
                }
                setShowVotation(true);
              }
            }
          } catch (err) {
            console.warn('Error procesando manualVotationTrigger en Monitor:', err);
          }
        }

        setSyncStatus('live');
      }
    }, (error) => {
      console.error('Error escuchando Firestore en Monitor:', error);
      setSyncStatus('offline');
    });

    return () => {
      if (eventSource) {
        try {
          eventSource.close();
        } catch (e) { }
      }
      unsubscribe();
    };
  }, [effectiveUserId]);

  // ── 2. Presencia de Dispositivo en Tiempo Real (Subcolección connected_monitors) ──
  useEffect(() => {
    if (!effectiveUserId) return;

    let deviceId = localStorage.getItem('garaoke_monitor_device_id');
    if (!deviceId) {
      deviceId = 'dev_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
      localStorage.setItem('garaoke_monitor_device_id', deviceId);
    }

    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    const joinedAt = Date.now();

    const reportPresence = async () => {
      try {
        const monitorRef = doc(db, 'users', effectiveUserId, 'connected_monitors', deviceId);
        await setDoc(monitorRef, {
          deviceId,
          displayName: user?.displayName || 'Invitado en Monitor',
          email: user?.email || null,
          photoURL: user?.photoURL || null,
          deviceType: isMobile ? 'Móvil' : 'Escritorio',
          lastSeen: Date.now(),
          joinedAt,
        }, { merge: true });
      } catch (err) {
        console.warn('Error registrando presencia del monitor:', err);
      }
    };

    const cleanupPresence = async () => {
      try {
        const monitorRef = doc(db, 'users', effectiveUserId, 'connected_monitors', deviceId);
        await deleteDoc(monitorRef);
      } catch (err) { }
    };

    reportPresence();
    const interval = setInterval(reportPresence, 15000);

    const handleBeforeUnload = () => {
      cleanupPresence();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanupPresence();
    };
  }, [effectiveUserId, user]);

  // ── 3. Sincronización en Tiempo Real de Velocidad de Reproducción (Playback Rate) ──
  useEffect(() => {
    if (!tar || typeof tar.setPlaybackRate !== 'function') return;
    const rate = Number(playbackState.playbackRate) || 1.0;
    try {
      if (typeof tar.getPlaybackRate === 'function') {
        const currentRate = tar.getPlaybackRate();
        if (Math.abs(currentRate - rate) > 0.01) {
          console.log(`[Monitor Sync] Aplicando nueva velocidad de reproducción: ${rate}x (anterior: ${currentRate}x)`);
          tar.setPlaybackRate(rate);
        }
      } else {
        tar.setPlaybackRate(rate);
      }
    } catch (e) {
      console.warn('Error sincronizando playbackRate en Monitor:', e);
    }
  }, [playbackState.playbackRate, tar]);

  // ── 4. Sincronización Directa: seekTo(fbVideoTime + syncOffsetSec) cuando llega heartbeat ──
  // Esta es la fuente de verdad: el Player escribe currentVideoTime cada N segundos en Firebase,
  // y el Monitor simplemente hace seekTo a esa posición + tolerancia si el desfasaje supera el umbral.
  useEffect(() => {
    if (!tar || typeof tar.getCurrentTime !== 'function') return;
    if (!playbackState.videoId || playbackState.status !== 'PLAYING') return;
    // Solo sincronizar si el heartbeat corresponde al video actualmente cargado en el monitor
    if (fbVideoId && fbVideoId !== videoId && fbVideoId !== playbackState.videoId) return;
    if (fbVideoTime <= 0) return;

    const targetTime = Math.max(0, fbVideoTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0));

    try {
      const actualPlayerTime = tar.getCurrentTime();
      const drift = Math.abs(actualPlayerTime - targetTime);

      if (drift > driftThreshold && targetTime < (totalVideoTime || 99999)) {
        console.log(`[Monitor Sync Directo] Drift ${drift.toFixed(1)}s > ${driftThreshold}s → seekTo(${targetTime.toFixed(1)}s) [fb=${fbVideoTime}s + offset=${syncOffsetSec}s + userTol=${userTolerance}s]`);
        tar.seekTo(targetTime, true);
        if (typeof tar.playVideo === 'function') {
          tar.playVideo();
        }
      }
    } catch (e) {
      console.warn('Error en sincronización directa del Monitor:', e);
    }
  }, [fbVideoTime, fbVideoId, tar, playbackState.videoId, playbackState.status, videoId, syncOffsetSec, userTolerance, driftThreshold, totalVideoTime]);

  // ── 5. Propagación en Caliente: Reajuste si el Host Modifica la Tolerancia Global ──
  useEffect(() => {
    const hostOffset = Number(syncOffsetSec) || 0;
    if (prevSyncOffsetRef.current !== hostOffset) {
      const prev = prevSyncOffsetRef.current;
      prevSyncOffsetRef.current = hostOffset;
      if (tar && typeof tar.getCurrentTime === 'function' && playbackState.status === 'PLAYING') {
        const delta = hostOffset - prev;
        const curTime = tar.getCurrentTime();
        const newTarget = Math.max(0, curTime + delta);
        console.log(`[Tolerancia Host Sync] Cambio global: ${prev}s → ${hostOffset}s. Seek relativo a ${newTarget.toFixed(1)}s`);
        try {
          tar.seekTo(newTarget, true);
          if (typeof tar.playVideo === 'function') tar.playVideo();
        } catch (e) { }
      }
    }
  }, [syncOffsetSec, tar, playbackState.status]);

  // ── 6. Motor de Progreso Visual + Votación Automática ──
  // Lee el tiempo directamente del reproductor local de YouTube (sin interpolar)
  useEffect(() => {
    const timer = setInterval(() => {
      if (!playbackState.videoId || playbackState.status !== 'PLAYING') {
        if (playbackState.status === 'PAUSED') {
          setCurrentProgressTime(fbVideoTime || 0);
        }
        return;
      }

      // Leer tiempo real del reproductor local del Monitor
      if (tar && typeof tar.getCurrentTime === 'function') {
        try {
          const localTime = tar.getCurrentTime();
          setCurrentProgressTime(localTime);
        } catch (e) { }
      }

      // Disparar votación automática considerando si el track tiene endTime configurado o la duración real del video
      const votationTriggerSec = Number(import.meta.env?.VITE_VOTATION_TRIGGER_BEFORE_END_SEC) || 15;
      const effectiveAssigned = resolveAssignedSinger(currentVideoData?.videoId, playlist, currentVideoData, playbackState);
      const hasAssignedPerformer = Boolean(effectiveAssigned && effectiveAssigned.length > 0 && effectiveAssigned.toLowerCase() !== 'cantante');
      const localProgress = tar && typeof tar.getCurrentTime === 'function' ? tar.getCurrentTime() : 0;

      // Si el track tiene configurado endTime se usa ese valor; de lo contrario se usa totalVideoTime
      const configuredEndSec = Math.max(0, Number(playbackState?.endTime ?? currentVideoData?.endTime ?? 0) || 0);
      const effectiveEndSec = (configuredEndSec > 0) ? configuredEndSec : totalVideoTime;
      const triggerTargetTime = Math.max(0, effectiveEndSec - votationTriggerSec);

      if (
        hasAssignedPerformer &&
        effectiveEndSec > (votationTriggerSec + 2) &&
        localProgress >= triggerTargetTime &&
        lastVotedSongRef.current !== currentVideoData?.videoId
      ) {
        lastVotedSongRef.current = currentVideoData?.videoId;
        if (currentVideoData && (!currentVideoData.assignedTo || currentVideoData.assignedTo !== effectiveAssigned)) {
          setCurrentVideoData(prev => ({ ...prev, assignedTo: effectiveAssigned }));
        }
        setShowVotation(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [playbackState, tar, totalVideoTime, currentVideoData, fbVideoTime, playlist, resolveAssignedSinger]);

  // Sincronizar pausa y reproducción del reproductor según el estado remoto
  useEffect(() => {
    if (!tar || typeof tar.getPlayerState !== 'function') return;
    try {
      if (!isMonitorEnabled || playbackState.status === 'PAUSED') {
        tar.pauseVideo();
      } else if (playbackState.status === 'PLAYING') {
        tar.playVideo();
      }
    } catch (e) { }
  }, [playbackState.status, isMonitorEnabled, tar]);

  // Manejar cambio de tamaño responsivo de la pantalla
  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onVideoReady = (event) => {
    if (event.target) {
      setTar(event.target);
      try {
        const duration = event.target.getDuration();
        setTotalVideoTime(duration || 0);

        // Aplicar inmediatamente la velocidad de reproducción remota
        const rate = Number(playbackState.playbackRate) || 1.0;
        if (typeof event.target.setPlaybackRate === 'function') {
          event.target.setPlaybackRate(rate);
        }

        // Siempre reproducir silenciado en el monitor de invitados
        event.target.mute();

        // Sincronización directa: seekTo(fbVideoTime + syncOffsetSec + userTolerance + bufferLatency) inmediato
        if (playbackState.videoId && playbackState.status === 'PLAYING' && fbVideoTime > 0) {
          const targetTime = Math.max(0, fbVideoTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0) + bufferLatency);
          if (targetTime > 0.5 && targetTime < (duration || 99999)) {
            console.log(`[Monitor onReady Sync] Posicionando video en ${targetTime.toFixed(1)}s [fb=${fbVideoTime}s + offset=${syncOffsetSec}s + userTol=${userTolerance}s + latency=${bufferLatency}s]`);
            event.target.seekTo(targetTime, true);
          }
          event.target.playVideo();
        }
      } catch (err) { }
    }
  };

  const onVideoEnd = () => {
    setVideoId('');
    setTar(null);
    setTotalVideoTime(0);
    setCurrentProgressTime(0);
    initialSyncedVidRef.current = null;
  };

  const onVideoError = (err) => {
    console.warn('Error en video de YouTube en Monitor:', err);
  };

  const handleManualSync = () => {
    if (tar && playbackState.videoId && fbVideoTime > 0) {
      const targetTime = Math.max(0, fbVideoTime + (Number(syncOffsetSec) || 0) + (Number(userTolerance) || 0) + bufferLatency);
      console.log(`[Manual Sync] Seek a ${targetTime.toFixed(1)}s [fb=${fbVideoTime}s + offset=${syncOffsetSec}s + userTol=${userTolerance}s + latency=${bufferLatency}s]`);
      try {
        tar.seekTo(targetTime, true);
        if (typeof tar.playVideo === 'function') tar.playVideo();
      } catch (e) { }
    }
  };

  // ── Ajuste de Tolerancia en Tiempo Real para Invitados (±step, compensando latencia del spinner de YouTube) ──
  const handleAdjustTolerance = useCallback((delta) => {
    setUserTolerance((prev) => {
      const nextVal = Math.round((prev + delta) * 10) / 10;
      const clamped = Math.max(-maxUserTolerance, Math.min(maxUserTolerance, nextVal));
      try {
        localStorage.setItem('garaoke_user_sync_tolerance', String(clamped));
      } catch (e) { }

      // Salto relativo instantáneo sobre la reproducción en curso compensando la latencia del spinner
      if (tar && typeof tar.getCurrentTime === 'function') {
        try {
          const curTime = tar.getCurrentTime();
          const targetTime = Math.max(0, curTime + delta + bufferLatency);
          console.log(`[Monitor Adjust Tolerance] Salto compensado: cur=${curTime.toFixed(1)}s, delta=${delta > 0 ? '+' : ''}${delta}s, spinnerLatency=${bufferLatency}s -> seekTo(${targetTime.toFixed(1)}s) [tolerancia total: ${clamped}s]`);
          tar.seekTo(targetTime, true);
          if (typeof tar.playVideo === 'function') tar.playVideo();
        } catch (err) { }
      }

      return clamped;
    });
  }, [tar, maxUserTolerance, bufferLatency]);

  // ── Restablecer Tolerancia Neutral (0.0s) al pulsar el botón central ──
  const handleResetTolerance = useCallback(() => {
    setUserTolerance((prev) => {
      if (prev === 0) return 0;
      try {
        localStorage.setItem('garaoke_user_sync_tolerance', '0');
      } catch (e) { }
      if (tar && typeof tar.getCurrentTime === 'function') {
        try {
          const curTime = tar.getCurrentTime();
          const targetTime = Math.max(0, curTime - prev + bufferLatency);
          console.log(`[Monitor Reset Tolerance] Restableciendo a 0.0s (compensado): cur=${curTime.toFixed(1)}s -> seekTo(${targetTime.toFixed(1)}s) [delta: ${-prev}s, spinnerLatency: ${bufferLatency}s]`);
          tar.seekTo(targetTime, true);
          if (typeof tar.playVideo === 'function') tar.playVideo();
        } catch (err) { }
      }
      return 0;
    });
  }, [tar, bufferLatency]);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.().catch(() => { });
    } else {
      document.exitFullscreen?.().catch(() => { });
    }
  };

  // ── 3. Votación Atómica Concurrente (30+ usuarios) ──
  const saveVotation = useCallback(async (votation) => {
    const vidId = currentVideoDataAux?.videoId || currentVideoData?.videoId;
    if (!votation || !vidId) return;

    lastVotedSongRef.current = vidId;

    const resolvedPerformer = resolveAssignedSinger(vidId, playlist, currentVideoData, playbackState)
      || currentVideoDataAux?.assignedTo
      || currentVideoData?.assignedTo
      || (playlist.find(it => it.videoId === vidId)?.assignedTo)
      || '';
    const performer = (resolvedPerformer && resolvedPerformer.trim().toLowerCase() !== 'cantante')
      ? resolvedPerformer.trim()
      : (resolvedPerformer || 'Cantante');

    let guestId = user?.uid;
    if (!guestId) {
      guestId = localStorage.getItem('garaoke_monitor_device_id');
      if (!guestId) {
        guestId = 'guest_' + Math.random().toString(36).substring(2, 11);
        localStorage.setItem('garaoke_monitor_device_id', guestId);
      }
    }
    const guestName = user?.displayName || 'Invitado';

    console.log(`🗳️ [Monitor Votación] Registrando voto: ${votation} ⭐ para "${performer || 'Cantante'}" (videoId: ${vidId}, guest: ${guestId})`);

    // 1. Guardar atómicamente en la subcolección 'votes' del anfitrión (concurrencia segura sin race conditions)
    if (effectiveUserId) {
      try {
        const voteDocId = `${vidId}_${guestId}`;
        const voteRef = doc(db, 'users', effectiveUserId, 'votes', voteDocId);
        await setDoc(voteRef, {
          videoId: vidId,
          performer: performer,
          assignedTo: performer,
          vote: Number(votation),
          stars: Number(votation),
          userId: guestId,
          userName: guestName,
          timestamp: Date.now(),
        }, { merge: true });
        console.log('✅ Voto guardado exitosamente en subcolección Firestore "votes"');
      } catch (err) {
        console.warn('Error guardando en subcolección votes de Firestore:', err);
      }
    }

    // 2. Notificar vía BroadcastChannel para actualización local instantánea
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const bc = new BroadcastChannel('garaoke_player_sync');
        bc.postMessage({
          type: 'VOTE_SUBMITTED',
          videoId: vidId,
          performer: performer || 'Cantante',
          assignedTo: performer || 'Cantante',
          vote: Number(votation),
          stars: Number(votation),
          userId: guestId,
          userName: guestName,
          timestamp: Date.now(),
        });
        bc.close();
      }
    } catch (e) {}

    // 3. Enviar al endpoint backend Express SSE si está disponible
    const syncBackendEnabled = isSyncBackendAvailable;
    if (syncBackendEnabled && effectiveUserId) {
      try {
        fetch(`${apiBaseUrl}/sync/vote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hostId: effectiveUserId,
            videoId: vidId,
            userId: guestId,
            userName: guestName,
            vote: Number(votation),
          }),
        }).then(res => {
          if (res.ok) console.log('✅ Voto registrado en backend Express');
        }).catch(err => {
          console.warn('Backend vote request failed:', err);
        });
      } catch (err) {}
    }

    // 4. Actualizar el array embebido de la playlist si el host o permisos lo permiten
    try {
      let plist = [...playlist];
      const itemIndex = plist.findIndex((it) => it.videoId === vidId);
      if (itemIndex !== -1) {
        const item = { ...plist[itemIndex] };
        item.votes = item.votes || [];
        const existingVoteIndex = item.votes.findIndex((v) => v.userId === guestId);
        if (existingVoteIndex !== -1) {
          item.votes[existingVoteIndex] = {
            userId: guestId,
            userName: guestName,
            vote: Number(votation),
          };
        } else {
          item.votes.push({
            userId: guestId,
            userName: guestName,
            vote: Number(votation),
          });
        }
        plist[itemIndex] = item;
        if (effectiveUserId && typeof updateUserField === 'function') {
          updateUserField('playlist', plist, effectiveUserId);
        }
      }
    } catch (err) {
      console.warn('Advertencia en actualización de playlist votes:', err);
    }
  }, [currentVideoDataAux, currentVideoData, playlist, effectiveUserId, user, updateUserField, resolveAssignedSinger, playbackState]);

  useEffect(() => {
    if (showVotation === false && stars > 0) {
      saveVotation(stars);
      setStars(0);
    }
  }, [showVotation, stars, saveVotation]);

  const formatTime = (secs) => {
    const s = Math.floor(secs || 0);
    const m = Math.floor(s / 60);
    const remSecs = s % 60;
    return `${m}:${remSecs < 10 ? '0' : ''}${remSecs}`;
  };

  const trackStartSec = Math.max(0, Number(playbackState?.startTime ?? currentVideoData?.startTime ?? 0) || 0);
  const trackEndSec = Math.max(0, Number(playbackState?.endTime ?? currentVideoData?.endTime ?? 0) || 0);
  const effectiveTotalTime = (trackEndSec > 0) ? Math.min(totalVideoTime > 0 ? totalVideoTime : trackEndSec, trackEndSec) : totalVideoTime;
  const trackSpan = effectiveTotalTime - trackStartSec;

  const progressPercent = (trackSpan > 0 && currentProgressTime >= trackStartSec)
    ? Math.min(100, Math.max(0, ((currentProgressTime - trackStartSec) / trackSpan) * 100))
    : (effectiveTotalTime > 0 ? Math.min(100, (currentProgressTime / effectiveTotalTime) * 100) : 0);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        ref={containerRef}
        sx={{
          height: '100dvh',
          minHeight: '-webkit-fill-available',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #090a12 0%, #111322 100%)',
          color: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          fontFamily: '"Outfit", "Inter", sans-serif',
        }}
      >
        {/* Modal de Votación Gamer Neón */}
        {showVotation && currentVideoData && currentVideoData.videoId && (
          <VotationModal
            targetVideo={tar}
            currentVideoData={currentVideoData}
            playlist={playlist}
            assignedPerformer={
              resolveAssignedSinger(currentVideoData.videoId, playlist, currentVideoData, playbackState)
              || currentVideoData?.assignedTo
              || ''
            }
            setShowVotation={setShowVotation}
            updateUserField={updateUserField}
            userId={effectiveUserId}
            stars={stars}
            setStars={setStars}
            durationSec={Number(import.meta.env?.VITE_VOTATION_DURATION_SEC) || 10}
          />
        )}

        {/* Overlay de Monitores Desactivados por el Anfitrión */}
        {!isMonitorEnabled && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 9999,
              background: 'rgba(9, 10, 18, 0.96)',
              backdropFilter: 'blur(20px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px 24px',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255, 183, 3, 0.12)',
                border: '2px solid rgba(255, 183, 3, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: '0 0 35px rgba(255, 183, 3, 0.3)',
                animation: 'pulseGlow 2.5s infinite ease-in-out',
                '@keyframes pulseGlow': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 0.85 },
                  '50%': { transform: 'scale(1.08)', opacity: 1 },
                },
              }}
            >
              <WifiOffIcon sx={{ fontSize: '42px', color: '#ffb703' }} />
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#ffffff',
                mb: 1.5,
                fontSize: { xs: '1.3rem', sm: '1.6rem' },
                letterSpacing: '-0.3px',
              }}
            >
              Monitores Momentáneamente Desactivados
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: '460px',
                color: 'rgba(255, 255, 255, 0.65)',
                lineHeight: 1.6,
                mb: 3.5,
                fontSize: '0.95rem',
              }}
            >
              El anfitrión ha pausado temporalmente el servicio de monitores interactivos para optimizar la conectividad del evento.
            </Typography>

            <Chip
              icon={<FiberManualRecordIcon sx={{ fontSize: '10px !important', color: '#ffb703 !important' }} />}
              label="Esperando reactivación automática..."
              sx={{
                backgroundColor: 'rgba(255, 183, 3, 0.12)',
                border: '1px solid rgba(255, 183, 3, 0.35)',
                color: '#ffb703',
                fontWeight: 700,
                fontSize: '0.82rem',
                paddingX: '10px',
                height: '32px',
              }}
            />
          </Box>
        )}

        {/* ── BARRA SUPERIOR GAMER (Touch-Friendly + Safe Area) ── */}
        <Box
          sx={{
            minHeight: '52px',
            paddingTop: 'max(8px, env(safe-area-inset-top))',
            paddingBottom: '8px',
            paddingLeft: 'max(14px, env(safe-area-inset-left))',
            paddingRight: 'max(14px, env(safe-area-inset-right))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(18, 20, 32, 0.85)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          {/* Logo y Badge LIVE */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: '1.05rem',
                letterSpacing: '-0.3px',
                background: 'linear-gradient(135deg, #ffffff 40%, #00d4ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Garaoke
            </Typography>
            <Chip
              size="small"
              label="EN VIVO"
              sx={{
                height: '20px',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.5px',
                background: 'rgba(0, 212, 255, 0.15)',
                color: '#00d4ff',
                border: '1px solid rgba(0, 212, 255, 0.35)',
              }}
            />
          </Box>

          {/* Chip del Cantante Activo */}
          {currentVideoData?.assignedTo && (
            <Chip
              size="small"
              icon={<span style={{ fontSize: '12px' }}>🎤</span>}
              label={currentVideoData.assignedTo}
              sx={{
                height: '24px',
                fontSize: '0.75rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.25), rgba(157, 78, 221, 0.25))',
                border: '1px solid rgba(255, 45, 155, 0.45)',
                color: '#ffffff',
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
          )}

          {/* Controles Rápidos: Sincro, Pantalla Completa (sin desmutear) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title="Resincronizar">
              <IconButton size="small" onClick={handleManualSync} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <SyncIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pantalla Completa">
              <IconButton size="small" onClick={toggleFullscreen} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── CONTENIDO PRINCIPAL: VIDEO O SALA DE ESPERA ── */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#000000',
            overflow: 'hidden',
          }}
        >
          {videoId ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& iframe': {
                  width: '100%',
                  height: '100%',
                  border: 'none',
                },
              }}
            >
              <YouTube
                videoId={videoId}
                opts={opts}
                onReady={onVideoReady}
                onEnd={onVideoEnd}
                onError={onVideoError}
                onStateChange={(e) => {
                  // CUED (5) o PAUSED (2): forzar reproducción si el estado remoto es PLAYING
                  if (e?.data === 5 || e?.data === 2) {
                    if (playbackState.status === 'PLAYING' && isMonitorEnabled) {
                      try { e.target.playVideo(); } catch (err) { }
                    }
                  }
                }}
                style={{ width: '100%', height: '100%' }}
              />
            </Box>
          ) : (
            /* ── Pantalla de Espera Gamer Neón ── */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                textAlign: 'center',
                width: '100%',
                maxWidth: '420px',
              }}
            >
              {/* Ecualizador Animado Gamer */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: '5px',
                  height: '50px',
                  marginBottom: '20px',
                }}
              >
                {[0.4, 0.8, 1.0, 0.6, 0.9, 0.5, 0.7].map((scale, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: '6px',
                      borderRadius: '3px',
                      background: i % 2 === 0
                        ? 'linear-gradient(180deg, #ff2d9b, #9d4edd)'
                        : 'linear-gradient(180deg, #00d4ff, #0077b6)',
                      boxShadow: i % 2 === 0
                        ? '0 0 10px rgba(255, 45, 155, 0.5)'
                        : '0 0 10px rgba(0, 212, 255, 0.5)',
                      animation: `equalizeBar 1.2s ease-in-out infinite alternate ${i * 0.15}s`,
                      '@keyframes equalizeBar': {
                        '0%': { height: '8px' },
                        '100%': { height: `${Math.round(48 * scale)}px` },
                      },
                    }}
                  />
                ))}
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  background: 'linear-gradient(135deg, #ffffff, #00d4ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px',
                }}
              >
                {!effectiveUserId ? 'Monitor no vinculado' : 'Esperando la próxima pista...'}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.86rem',
                  marginBottom: '24px',
                  lineHeight: 1.4,
                }}
              >
                {!effectiveUserId
                  ? 'Para sincronizar este monitor, abre el enlace escaneando el código QR de la pantalla principal o agrega ?UID=<id_usuario> a la URL.'
                  : 'El anfitrión está preparando el siguiente tema en el escenario.'
                }
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── BARRA INFERIOR DE PROGRESO, TOLERANCIA Y CONTROLES (Touch-Friendly + Safe Area) ── */}
        {!showVotation && (
          <Box
            sx={{
              background: 'rgba(12, 14, 24, 0.96)',
              backdropFilter: 'blur(16px)',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              paddingTop: '8px',
              paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
              paddingLeft: 'max(14px, env(safe-area-inset-left))',
              paddingRight: 'max(14px, env(safe-area-inset-right))',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            {/* Fila 1: Barra de Progreso Neón y Tiempo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #ff2d9b, #00d4ff)',
                    borderRadius: '2px',
                    boxShadow: '0 0 8px #00d4ff',
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.55)',
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                {formatTime(currentProgressTime)} / {formatTime(effectiveTotalTime)}
              </Typography>
            </Box>

            {/* Fila 2: Botones de Acción (Retrasar [izq], Tolerancia [centro], Adelantar [der]) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 2 },
                width: '100%',
              }}
            >
              {/* Botón Retrasar (Alineado a la Izquierda) */}
              <Button
                variant="outlined"
                onClick={() => handleAdjustTolerance(-adjustStep)}
                disabled={userTolerance <= -maxUserTolerance}
                startIcon={<FastRewindIcon sx={{ fontSize: '1.2rem !important' }} />}
                sx={{
                  flex: 1,
                  height: '44px',
                  color: '#ff4d6d',
                  borderColor: 'rgba(255, 77, 109, 0.45)',
                  background: 'rgba(255, 77, 109, 0.08)',
                  borderRadius: '12px',
                  fontSize: { xs: '0.78rem', sm: '0.85rem' },
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 1.5,
                  minWidth: 0,
                  boxShadow: '0 2px 8px rgba(255, 77, 109, 0.15)',
                  '&:hover': {
                    borderColor: '#ff4d6d',
                    background: 'rgba(255, 77, 109, 0.22)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    boxShadow: 'none',
                  }
                }}
              >
                retrasar
              </Button>

              {/* Botón Central de Tolerancia Aplicada (Interactivo para entender estado y restablecer) */}
              <Tooltip title={userTolerance !== 0 ? 'Tolerancia activa. Haz clic para restablecer a 0.0s' : 'Tolerancia en 0.0s (neutral)'}>
                <Button
                  variant="outlined"
                  onClick={handleResetTolerance}
                  sx={{
                    flex: { xs: 1.1, sm: 1 },
                    height: '44px',
                    borderRadius: '12px',
                    fontSize: { xs: '0.78rem', sm: '0.85rem' },
                    fontWeight: 800,
                    textTransform: 'none',
                    px: 1,
                    minWidth: 0,
                    color: userTolerance === 0
                      ? 'rgba(255, 255, 255, 0.7)'
                      : userTolerance > 0
                        ? '#00e5ff'
                        : '#ff4d6d',
                    background: userTolerance === 0
                      ? 'rgba(255, 255, 255, 0.05)'
                      : userTolerance > 0
                        ? 'rgba(0, 229, 255, 0.12)'
                        : 'rgba(255, 77, 109, 0.12)',
                    borderColor: userTolerance === 0
                      ? 'rgba(255, 255, 255, 0.18)'
                      : userTolerance > 0
                        ? 'rgba(0, 229, 255, 0.5)'
                        : 'rgba(255, 77, 109, 0.5)',
                    boxShadow: userTolerance !== 0
                      ? `0 0 12px ${userTolerance > 0 ? 'rgba(0, 229, 255, 0.2)' : 'rgba(255, 77, 109, 0.2)'}`
                      : 'none',
                    '&:hover': {
                      background: userTolerance === 0
                        ? 'rgba(255, 255, 255, 0.1)'
                        : userTolerance > 0
                          ? 'rgba(0, 229, 255, 0.22)'
                          : 'rgba(255, 77, 109, 0.22)',
                      borderColor: userTolerance === 0
                        ? 'rgba(255, 255, 255, 0.35)'
                        : userTolerance > 0
                          ? '#00e5ff'
                          : '#ff4d6d',
                    }
                  }}
                >
                  {userTolerance === 0 ? '0.0s' : `${userTolerance > 0 ? '+' : ''}${userTolerance.toFixed(1)}s ↺`}
                </Button>
              </Tooltip>

              {/* Botón Adelantar (Alineado a la Derecha) */}
              <Button
                variant="outlined"
                onClick={() => handleAdjustTolerance(adjustStep)}
                disabled={userTolerance >= maxUserTolerance}
                endIcon={<FastForwardIcon sx={{ fontSize: '1.2rem !important' }} />}
                sx={{
                  flex: 1,
                  height: '44px',
                  color: '#00e5ff',
                  borderColor: 'rgba(0, 229, 255, 0.45)',
                  background: 'rgba(0, 229, 255, 0.08)',
                  borderRadius: '12px',
                  fontSize: { xs: '0.78rem', sm: '0.85rem' },
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 1.5,
                  minWidth: 0,
                  boxShadow: '0 2px 8px rgba(0, 229, 255, 0.15)',
                  '&:hover': {
                    borderColor: '#00e5ff',
                    background: 'rgba(0, 229, 255, 0.22)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    boxShadow: 'none',
                  }
                }}
              >
                adelantar
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default Monitor;
